import { AnimatePresence } from 'framer-motion'
import TopBar from 'layout/TopBar'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Toaster } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Route, Routes, useLocation, useNavigate } from 'react-router'
import styled, { css, ThemeProvider } from 'styled-components'

import Tickets from '@/features/Tickets'

import Inactivity from './components/Inactivity'
import Checkout from './features/Checkout'
import useCheckout from './features/Checkout/hooks/useCheckout'
import Intro from './features/Intro'
import useLayout from './layout/useLayout'
import { invokeAndroidMethod } from './lib/androidBridge'
import { normalizeLanguageCode } from './lib/language'
import { Cover, TransitionSecondary } from './lib/mixins'
import { useAppQuery } from './lib/queries'
import theme from './lib/theme'
import Style from './style'

const AppWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const AppHeader = styled.div`
  position: relative;
  z-index: 100;

  ${props =>
    props.theme.accessibilityMode &&
    css`
      order: 2;
    `};

  &:before {
    ${Cover()}
    background-color: var(--color-global);
    transform: scaleY(0);
    visibility: hidden;
    transform-origin: center top;
    backface-visibility: hidden;
    ${TransitionSecondary('transform, visibility')}
  }
`

const AppContent = styled.div`
  position: relative;
  flex: 1 1 auto;
`

const AppError = styled.div`
  ${Cover()}
  background-color:rgba(38, 42, 70, 0.8);
  backdrop-filter: blur(15px);
  color: ${props => props.theme.color.inverted};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  padding: 1rem 5%;
  z-index: 100;
  font-size: 3rem;

  ${props =>
    props.theme.contrastMode &&
    css`
      background-color: rgba(0, 0, 0, 0.8);
    `};
`

const AppToaster = () => {
  const { contrastMode, fontSize, accessibilityMode } = useLayout()
  const currentTheme = theme({ contrastMode, fontSize, accessibilityMode })

  const modalRoot = document.querySelector('#modal')
  if (!modalRoot) return null

  return createPortal(
    <Toaster
      position="bottom-center"
      gutter={8}
      toastOptions={{
        duration: 5000,
        removeDelay: 1000,
        style: {
          background: currentTheme.color.primary,
          color: currentTheme.color.inverted,
          borderRadius: 0,
          padding: '0.5rem 1.25rem',
          minWidth: '50vw'
        }
      }}
    />,
    modalRoot
  )
}

const App = () => {
  const location = useLocation()
  const { contrastMode, fontSize, accessibilityMode, resetLayout, setAppData } = useLayout()
  const { reset: resetCheckout, printerStatus } = useCheckout()
  const currentTheme = theme({ contrastMode, fontSize, accessibilityMode })
  const pathname = location.pathname
  const { data: appData } = useAppQuery({
    refetchInterval: 1000 * 60 * 5 // 5 minutes
  })
  const [showInactivity, setShowInactivity] = useState(false)
  const [inactivityInstance, setInactivityInstance] = useState(0)
  const navigate = useNavigate()
  const { i18n, t } = useTranslation()
  const appliedConfigLanguageRef = useRef<string | null>(null)

  useEffect(() => {
    if (appData) {
      setAppData(appData)
    }
  }, [appData])

  useEffect(() => {
    const defaultLanguage = (appData as { defaultLanguage?: unknown } | null | undefined)?.defaultLanguage
    if (typeof defaultLanguage !== 'string') {
      return
    }

    const configLanguage = normalizeLanguageCode(defaultLanguage)
    if (appliedConfigLanguageRef.current === configLanguage) {
      return
    }

    appliedConfigLanguageRef.current = configLanguage
    const currentLanguage = normalizeLanguageCode(i18n.resolvedLanguage ?? i18n.language)
    if (currentLanguage !== configLanguage) {
      void i18n.changeLanguage(configLanguage)
    }
  }, [appData, i18n])

  useEffect(() => {
    if (process.env.REACT_APP_API_CONTROLLER !== 'WEBVIEW') {
      return
    }

    const syncWebviewLanguage = async (languageCode: string) => {
      try {
        await invokeAndroidMethod<unknown>('setLanguage', normalizeLanguageCode(languageCode))
      } catch (error) {
        console.error('[bridge] setLanguage failed', error)
      }
    }

    const currentLanguage = normalizeLanguageCode(i18n.resolvedLanguage ?? i18n.language)
    void syncWebviewLanguage(currentLanguage)
    i18n.on('languageChanged', syncWebviewLanguage)

    return () => {
      i18n.off('languageChanged', syncWebviewLanguage)
    }
  }, [i18n])

  const resetAppState = useCallback(() => {
    resetCheckout()
    setShowInactivity(false)
    resetLayout()
    if (pathname !== '/') {
      navigate('/')
    }
  }, [navigate, pathname, resetCheckout, resetLayout])

  const handleSessionTimeout = useCallback(() => {
    if (window.location.pathname === '/') return
    setShowInactivity(true)
    setInactivityInstance(current => current + 1)
  }, [])

  const handleInactivityContinue = useCallback(() => {
    setShowInactivity(false)
  }, [])

  const handleInactivityReset = useCallback(() => {
    setShowInactivity(false)
    resetAppState()
  }, [resetAppState])

  useEffect(() => {
    const previousHandler = window.onSessionTimeout
    window.onSessionTimeout = handleSessionTimeout

    return () => {
      if (window.onSessionTimeout === handleSessionTimeout) {
        window.onSessionTimeout = previousHandler
      }
    }
  }, [handleSessionTimeout])

  useEffect(() => {
    const triggerKeepAlive = () => {
      if (!window.Android || typeof window.Android.keepAlive !== 'function') {
        return
      }

      window.Android.keepAlive()
    }

    window.addEventListener('click', triggerKeepAlive, true)
    window.addEventListener('touchstart', triggerKeepAlive, { passive: true, capture: true })
    window.addEventListener('scroll', triggerKeepAlive, { passive: true, capture: true })

    return () => {
      window.removeEventListener('click', triggerKeepAlive, true)
      window.removeEventListener('touchstart', triggerKeepAlive, true)
      window.removeEventListener('scroll', triggerKeepAlive, true)
    }
  }, [])

  return (
    <ThemeProvider theme={currentTheme}>
      <Style contrastMode={contrastMode} accessibilityMode={accessibilityMode} fontSize={fontSize} />
      <AppWrapper>
        <AppHeader>
          <TopBar />
        </AppHeader>
        <AppContent>
          <AnimatePresence mode="wait">
            <Routes location={location}>
              <Route path="/" element={<Intro />} />
              <Route path="/tickets" element={<Tickets />} />
              <Route path="/checkout" element={<Checkout />} />
            </Routes>
          </AnimatePresence>
          {printerStatus && !['READY', 'PAPER_NEAR_EMPTY'].includes(printerStatus) && (
            <AppError>
              {printerStatus === 'OFFLINE' && t('printerOffline')}
              {printerStatus === 'COVER_OPEN' && t('printerCoverOpen')}
              {printerStatus === 'NO_PAPER' && t('printerNoPaper')}
              {printerStatus === 'UNKNOWN_ERROR' && t('printerUnknownError')}
            </AppError>
          )}
        </AppContent>
        {showInactivity && (
          <Inactivity
            key={inactivityInstance}
            durationSeconds={5}
            onContinue={handleInactivityContinue}
            onTimeout={handleInactivityReset}
          />
        )}
      </AppWrapper>
      <AppToaster />
    </ThemeProvider>
  )
}

export default App
